import type { FeatureKey } from "./features";

export type NavGroup =
  | "main"
  | "care"
  | "pharmacy"
  | "crm"
  | "communications"
  | "front-desk"
  | "visitors"
  | "sales-assistant"
  | "purchasing"
  | "move-ins"
  | "maintenance"
  | "support"
  | "financials"
  | "workforce"
  | "knowledge"
  | "administration";

/** What the rail calls each section. */
export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  main: "Main",
  care: "Care & clinical",
  pharmacy: "Pharmacy",
  crm: "CRM & marketing",
  communications: "Communications",
  "front-desk": "Front desk",
  visitors: "Visitors",
  "sales-assistant": "Sales assistant",
  purchasing: "Purchasing",
  "move-ins": "Move-ins",
  maintenance: "Maintenance",
  support: "Support",
  financials: "Financials",
  workforce: "Workforce",
  knowledge: "Knowledge base",
  administration: "Administration",
};

/** The rail's section order is fixed, not inferred: a saved arrangement moves screens, and ordering
 * by the first screen to land in a section would reshuffle the rail when one item moves. */
export const NAV_GROUP_ORDER: readonly NavGroup[] = [
  "main",
  "care",
  "pharmacy",
  "crm",
  "communications",
  "front-desk",
  "visitors",
  "sales-assistant",
  "purchasing",
  "move-ins",
  "maintenance",
  "support",
  "financials",
  "workforce",
  "knowledge",
  "administration",
];

import { item, type NavItem, type SettingsScope } from "./navigation-item";
import { SETTINGS_NAVIGATION } from "./navigation-settings";
import { PLATFORM_NAVIGATION } from "./navigation-platform";

export type { NavItem, SettingsScope } from "./navigation-item";

/** One row per feature-bearing screen: the sidebar, route guard and settings console all render
 * from this, so a screen cannot appear without a permission and a feature. */
export const NAVIGATION: readonly NavItem[] = [
  item("dashboard", "Dashboard", "/", "main", "dashboard:read"),
  item("residents", "Residents", "/residents", "care", "resident:read"),
  item("activities", "Activities", "/activities", "care", "activity:read"),
  item("activity-calendar", "Activity calendar", "/activity-calendar", "care", "activity:read"),
  item("incidents", "Incidents", "/incidents", "care", "incident:read"),
  item("care-plans", "Care plans", "/care-plans", "care", "resident.clinical:read"),
  item("move-in", "Move-in", "/move-in", "care", "admission:read"),
  item("admissions", "Admissions", "/admissions", "care", "admission:read"),
  item("daily-roll", "Daily roll", "/daily-roll", "care", "report:read"),
  item("care-pass", "Care pass", "/care-pass", "care", "carepass:read"),
  item("notes-center", "Notes center", "/notes-center", "care", "note:read"),
  item("therapy-caseload", "Therapy caseload", "/therapy-caseload", "care", "therapy:read"),
  item(
    "therapy-cosign-queue",
    "Awaiting my co-signature",
    "/therapy-cosign-queue",
    "care",
    "therapy:cosign",
  ),
  item("therapy-payroll", "Therapist payroll", "/therapy-payroll", "care", "therapy:manage"),
  item(
    "care-plan-signature-queue",
    "Care plans to co-sign",
    "/care-plan-signature-queue",
    "care",
    "resident.clinical:update",
  ),
  item("pcp-visits", "PCP visit report", "/pcp-visits", "care", "report:read"),
  item("expiring-auths", "Expiring auths", "/expiring-auths", "care", "report:read"),
  item("survey-access", "Survey access", "/survey-access", "care", "resident:export"),
  item("med-pass", "Med pass", "/med-pass", "pharmacy", "medication:administer"),
  item("med-pass-report", "Med pass report", "/med-pass-report", "pharmacy", "medication:read"),
  item(
    "pharmacy-receiving",
    "Receiving & counts",
    "/pharmacy-receiving",
    "pharmacy",
    "medication:receive",
  ),
  item("supply-forecast", "Supply forecast", "/supply-forecast", "pharmacy", "medication:read"),
  item("crm", "CRM dashboard", "/crm", "crm", "crm:read"),
  item("leads", "Leads", "/crm/leads", "crm", "crm:read"),
  item("tours", "Tours", "/crm/tours", "crm", "crm:read"),
  item("referral-sources", "Referral sources", "/crm/referral-sources", "crm", "crm:read"),
  item("prospecting", "Prospecting", "/crm/prospecting", "crm", "crm:read"),
  item("crm-events", "Events", "/crm/events", "crm", "crm:read"),
  item("campaigns", "Campaigns", "/crm/campaigns", "crm", "crm:manage"),
  item("messaging", "Messages", "/messaging", "communications", "messaging:use"),
  // The family community: the building's feed, notes from family, video visits.
  item("community", "Community", "/community", "communications", "community:read"),
  item("family-visits", "Family visits", "/community/visits", "communications", "community:read", {
    surface: "hidden",
  }),
  item("meetings", "Meetings", "/messaging/meetings", "communications", "messaging:use", {
    surface: "hidden",
  }),
  // Inside Messages (its own line there); someone without messaging finds it under You.
  item("mentions", "Mentions", "/mentions", "communications", "note:read", { surface: "hidden" }),
  item("fax", "Fax & email", "/fax", "communications", "fax:read"),
  /** Operations, not configuration: somebody looks up a hospice's fax number mid-shift, so the
   * Directory sits in the rail beside the fax log, not the settings hub. */
  item("directory", "Directory", "/directory", "communications", "directory:read"),
  item("sarah-calls", "Front desk calls", "/sarah/calls", "front-desk", "frontdesk:read"),
  /* "Front Desk Messages" rather than "Messages": a pinned entry leaves its group, and a bare
   * "Messages" would be ambiguous beside the recruiter's inbox. */
  item("sarah-messages", "Front desk messages", "/sarah/messages", "front-desk", "frontdesk:read"),
  item(
    "sarah-analytics",
    "Front desk analytics",
    "/sarah/analytics",
    "front-desk",
    "frontdesk:read",
  ),
  /* Operations, not configuration, and the one screen here nobody opens at a
     desk: it is the wall tablet the front desk works off. Same read permission
     as the call log, because it shows the same calls. */
  item("sarah-board", "Handoff board", "/sarah/board", "front-desk", "frontdesk:read"),
  /* The sign-in sheet and the evacuation list: Core's, since every building needs them. Grace's
   * side of the door (her conversations, insights, the kiosk) is her workspace's. */
  item("visitors", "Visitors", "/visitors", "support", "visitor:read"),
  /* Shelby, the sales assistant. One screen in the rail — the conversations
     she has had — and her persona and her switches in the settings hub, the
     way the front desk's are. */
  item(
    "shelby-conversations",
    "Sales assistant",
    "/shelby/conversations",
    "sales-assistant",
    "salesagent:read",
  ),
  /* One Nancy row for her orders. Her settings open from the Nancy workspace rail; what she knows
   * is a hidden row at the same address so the roles editor files it under Nancy. Settings comes
   * first so that address is named for it. */
  item("nancy", "Nancy", "/ws/nancy", "purchasing", "nancy:read"),
  item("nancy-settings", "Nancy settings", "/ws/nancy/settings", "purchasing", "nancy:configure", {
    surface: "hidden",
  }),
  item("nancy-graph", "What Nancy knows", "/ws/nancy/settings", "purchasing", "nancy:read", {
    surface: "hidden",
  }),
  /* Amy: every open move-in, from the lead to the chart, at her workspace's front door. */
  item("move-in-agent", "Move-ins", "/ws/amy", "move-ins", "amy:read"),
  /* One row: the Agent works beside Menus in the right-hand pane, not on a screen of its own. */
  item("menu-planner", "Menus", "/menus", "purchasing", "menu:read"),
  item("supplies", "Supplies", "/supplies", "purchasing", "menu:read"),
  /* David: his conversation at his workspace's front door, then the work orders and the shop. */
  item("maintenance-agent", "Ask David", "/ws/david", "maintenance", "maintenance:read"),
  item("maintenance", "Work orders", "/maintenance/work-orders", "maintenance", "maintenance:read"),
  item("maintenance-shop", "Parts shop", "/maintenance/parts", "maintenance", "maintenance:read"),
  item("call-logs", "Call logs", "/call-logs", "crm", "crm:read", {
    surface: "hidden",
  }),
  item("task-center", "Task center", "/task-center", "support", "task:read"),
  item("transport", "Appointments", "/transport", "support", "schedule:read"),
  item("bulk-operations", "Bulk operations", "/bulk-operations", "financials", "financial:manage", {
    surface: "hidden",
  }),
  item("rent-roll", "Rent roll", "/rent-roll", "financials", "financial:read"),
  item("ar-summary", "AR summary", "/ar-summary", "financials", "financial:read"),
  item("collections", "Collections", "/collections", "financials", "financial:read"),
  item("census", "Census report", "/census", "financials", "report:read"),
  item(
    "monthly-responsibility",
    "Monthly responsibility",
    "/monthly-responsibility",
    "financials",
    "financial:read",
  ),
  item("charges", "Charge series", "/charges", "financials", "financial:manage"),
  item("invoice-roll", "Invoice roll", "/invoice-roll", "financials", "financial:manage"),
  item(
    "payments-received",
    "Payments received",
    "/payments-received",
    "financials",
    "financial:read",
  ),
  item("expense-entries", "Expense entries", "/expense-entries", "financials", "financial:manage"),
  item("vendors", "Vendors", "/vendors", "financials", "financial:manage"),
  item("pnl-report", "P&L report", "/pnl-report", "financials", "financial:read"),
  item("ap-report", "AP report", "/ap-report", "financials", "financial:read"),
  item(
    "trust-fund-report",
    "Trust fund report",
    "/trust-fund-report",
    "financials",
    "financial:read",
  ),
  /* Olivia herself: the conversation at her workspace's front door, above the screens she works. */
  item("workforce-agent", "Ask Olivia", "/ws/olivia", "workforce", "olivia:read"),
  item("hiring", "Hiring", "/hiring", "workforce", "hiring:read"),
  item("training", "Training", "/training", "workforce", "training:read"),
  item("hr", "HR", "/hr", "workforce", "hr:read"),
  /** Olivia's, with hiring, training and HR: who is on which shift is a workforce question.
   * Transport is a different screen (how a resident gets to an appointment); the permission is
   * shared because reading the diary and the rota were never split (statement.ts). */
  item("schedule", "Schedule", "/schedule", "workforce", "schedule:read"),
  item("policies", "Policies & procedures", "/policies", "knowledge", "policy:read"),
  /** `knowledgebase:read` opens a person's OWN folders and what was shared with them. The roll-up
   * reads across other people's drives, so it needs `knowledgebase:audit`. */
  item("knowledgebase", "Knowledgebase", "/knowledgebase", "knowledge", "knowledgebase:read"),
  item(
    "knowledgebase-rollup",
    "Knowledgebase roll-up",
    "/knowledgebase/rollup",
    "knowledge",
    "knowledgebase:audit",
  ),
  item("platform-console", "Organizations", "/platform", "administration", "platform:read", {
    platformOnly: true,
  }),
  ...PLATFORM_NAVIGATION,
  ...SETTINGS_NAVIGATION,
];

const NAV_BY_ID: Readonly<Record<string, NavItem>> = Object.fromEntries(
  NAVIGATION.map((entry) => [entry.id, entry]),
);

export function navItem(id: FeatureKey): NavItem {
  const found = NAV_BY_ID[id];
  if (!found) throw new Error(`No navigation entry for feature "${id}"`);
  return found;
}

/** The rail: operational screens only. */
export function sidebarNavigation(items: readonly NavItem[]): NavItem[] {
  return items.filter((entry) => (entry.surface ?? "sidebar") === "sidebar");
}

/** The hub's sections in the old hub's order, kept in step deliberately: people find a screen by
 * where it sits on the page, so a screen that moves category cannot be found. */
const SETTINGS_CATEGORIES = [
  "Users & access",
  "Organization",
  "Facility",
  "Phone & messaging",
  "Front desk",
  "Visitors",
  "Care",
  "Financials",
  "Directories",
  "Marketing & CRM",
  "Sales assistant",
  "Workforce",
  "Documents",
  "Therapies",
  "Billing & plan",
  "Personal",
] as const;

/** The settings hub, grouped by category in the order people already know. */
export function settingsNavigation(
  items: readonly NavItem[],
): Array<{ category: string; items: NavItem[] }> {
  const byCategory = new Map<string, NavItem[]>();
  for (const entry of items) {
    if (entry.surface !== "settings") continue;
    const category = entry.settingsCategory ?? "Other";
    byCategory.set(category, [...(byCategory.get(category) ?? []), entry]);
  }
  const order = (category: string) => {
    const index = (SETTINGS_CATEGORIES as readonly string[]).indexOf(category);
    return index === -1 ? SETTINGS_CATEGORIES.length : index;
  };
  return (
    [...byCategory.entries()]
      // oxlint-disable-next-line unicorn/no-array-sort -- a fresh array; the phone app's Hermes has no toSorted
      .sort(([a], [b]) => order(a) - order(b))
      .map(([category, entries]) => ({ category, items: entries }))
  );
}

/** The hub's top-level split: whose settings a section holds. */
export type SettingsSection = "organization" | "facility" | "personal" | "platform";

const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  "organization",
  "facility",
  "personal",
  "platform",
];

/** Shared lists and overridable defaults are read at the building in view, so they file under
 * it. */
export function settingsSectionOf(scope: SettingsScope): SettingsSection {
  return scope === "shared" || scope === "inherited" ? "facility" : scope;
}

/** The settings hub split by scope first, then by category inside each section. */
export function settingsSections(
  items: readonly NavItem[],
): Array<{ section: SettingsSection; categories: Array<{ category: string; items: NavItem[] }> }> {
  return SETTINGS_SECTIONS.map((section) => ({
    section,
    categories: settingsNavigation(
      items.filter(
        (entry) =>
          entry.surface === "settings" &&
          settingsSectionOf(entry.scope ?? "organization") === section,
      ),
    ),
  })).filter((group) => group.categories.length > 0);
}
